import PageSection from 'components/PageSection';
import Typography from '@mui/material/Typography';
import VerticalTable from 'components/VerticalTable';
import { Divider, useTheme, Grid } from '@mui/material';
import { Row } from 'components/Layout';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import GridContainer from 'components/GridContainer';
import calculateTargetValues from './calculate-target-values';
import useTrancheForAllocation from './use-tranche-for-allocation';
import { AutoAllocatingTranche } from 'further-types/allocations';
import Button from 'components/Button';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import api, { ApiResponses } from 'lib/trpcClient';
import getAutoAllocationTableFields from './get-auto-allocation-table-fields';
import { useEffect, useState } from 'react';
import { InitialTargetShareOption } from 'further-types/allocations';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import Spacing from 'components/Spacing';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { useDebounce } from 'usehooks-ts';
import { isValid } from 'date-fns';
import { FundAllocation } from '../index';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { ApiRouter } from 'further-api/trpc/routes';

interface FormValues {
  autoAllocatingTranches: Array<AutoAllocatingTranche>;
}

const AutoAllocations: React.FC<{
  selectedTrancheIds: Array<string>;
  dealSharePrice: number;
  allocationDate: Date | null;
  handleClearAndRestart: () => void;
  selectedCompanyId: string;
  onReview: () => void;
  formMethods: UseFormReturn<FormValues>;
  fundAutoAllocationResults: Array<FundAllocation>;
  setFundAutoAllocationResults: (results: Array<FundAllocation>) => void;
  initialTargetShareSelected: InitialTargetShareOption;
  setInitialTargetShareSelected: (value: InitialTargetShareOption) => void;
  resetInitialTargetShares: () => void;
  preservedAutoAllocationInput: Array<FundAllocation>;
  setShowPreviousShareSplit: (value: boolean) => void;
}> = ({
  selectedTrancheIds,
  dealSharePrice,
  allocationDate,
  handleClearAndRestart,
  selectedCompanyId,
  onReview,
  formMethods,
  fundAutoAllocationResults = [],
  setFundAutoAllocationResults,
  initialTargetShareSelected,
  setInitialTargetShareSelected,
  resetInitialTargetShares,
  preservedAutoAllocationInput,
  setShowPreviousShareSplit,
}) => {
  const notification = useNotification();
  const theme = useTheme();
  const restartDialog = useDisclosure();
  const [isDateChangeLoading, setIsDateChangeLoading] = useState(false);

  // All hooks must be called before any conditional logic
  const debouncedSelectedTrancheIds = useDebounce(selectedTrancheIds, 300);

  const { setValue, control, watch } = formMethods;

  // Watch for changes in defaultTransactionFeePercent and auto-set defaultTransactionFeeVatPercent to 0 when fee is 0
  const watchedTranches = watch('autoAllocatingTranches');
  useEffect(() => {
    if (watchedTranches) {
      watchedTranches.forEach((tranche, index) => {
        if (
          tranche.defaultTransactionFeePercent === 0 &&
          tranche.defaultTransactionFeeVatPercent !== 0
        ) {
          setValue(
            `autoAllocatingTranches.${index}.defaultTransactionFeeVatPercent`,
            0,
          );
        }
      });
    }
  }, [watchedTranches, setValue]);

  const {
    fields: autoAllocatingTranches,
    update,
    append,
  } = useFieldArray({
    control,
    name: 'autoAllocatingTranches',
  });

  const utils = api.useUtils();

  // Filter out removed tranches for display and merge with any existing Ran AutoAllocations data
  const visibleTranches = autoAllocatingTranches
    .filter((tranche) => !tranche.isRemoved)
    .map((tranche) => {
      // Find matching fund allocation data
      const fundAllocation = fundAutoAllocationResults.find(
        (fund) => fund.trancheId === tranche._id,
      );

      if (!fundAllocation) {
        return tranche;
      }

      return {
        ...tranche,
        totalSharesAllocated: fundAllocation.totalSharesAllocated,
        targetShares: fundAllocation.targetShares,
        investmentsAllocatedTo: fundAllocation.investmentsAllocatedTo,
        investmentsInTranche: fundAllocation.investmentsInTranche,
        totalCapitalAllocated: fundAllocation.totalCapitalAllocated,
      };
    });

  const updateUiBasedOnServerResponse = (
    err: TRPCClientErrorLike<ApiRouter>,
  ) => {
    if (err.data?.code === 'UI_UPDATE_ERROR') {
      setShowPreviousShareSplit(true);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  // Auto allocation mutation
  const mutationOptions = useHandleTrpcMutation<
    ApiResponses['eisAllocations']['runAutoAllocation']
  >()({
    onSuccess: (data) => {
      setFundAutoAllocationResults(data.allocationPreviews);
      setShowPreviousShareSplit(false);
      onReview();
    },
    onError: updateUiBasedOnServerResponse,
  });

  const { mutate: runAutoAllocation, isPending: isRunningAutoAllocation } =
    api.eisAllocations.runAutoAllocation.useMutation(mutationOptions);

  // Use the custom hook for tranche form state management
  const { isLoading: isLoadingTrancheForAllocation } = useTrancheForAllocation({
    selectedTrancheIds: debouncedSelectedTrancheIds,
    allocationDate,
    autoAllocatingTranches,
    fetchTrancheData: utils.eisAllocations.getTrancheDataByIds.fetch,
    onUpdate: update,
    onAppend: append,
    preservedAutoAllocationInput,
  });

  // Handle investment date changes - refetch all tranches while preserving specific fields
  useEffect(() => {
    if (
      selectedTrancheIds.length > 0 &&
      isValid(allocationDate) &&
      autoAllocatingTranches.length > 0
    ) {
      const handleInvestmentDateChange = async () => {
        setIsDateChangeLoading(true);
        try {
          // Extract preserved fields from current tranches
          const preservedFields = new Map<
            string,
            {
              minimumInvestorPortfolioSize: number;
              maximumSingleCompanyAllocation: number;
              initialTargetShares: number;
              defaultTransactionFeePercent: number;
              defaultTransactionFeeVatPercent: number;
              finalDeal: boolean;
              fixedTargetShares?: number;
            }
          >();

          autoAllocatingTranches.forEach((tranche) => {
            if (selectedTrancheIds.includes(tranche._id)) {
              preservedFields.set(tranche._id, {
                minimumInvestorPortfolioSize:
                  tranche.minimumInvestorPortfolioSize,
                maximumSingleCompanyAllocation:
                  tranche.maximumSingleCompanyAllocation,
                initialTargetShares: tranche.initialTargetShares,
                defaultTransactionFeePercent:
                  tranche.defaultTransactionFeePercent,
                defaultTransactionFeeVatPercent:
                  tranche.defaultTransactionFeeVatPercent,
                finalDeal: tranche.finalDeal,
                fixedTargetShares: tranche.fixedTargetShares,
              });
            }
          });

          // Create lookup from preservedAutoAllocationInput
          const preservedDraftLookup = new Map<
            string,
            {
              minimumInvestorPortfolioSize: number;
              maximumSingleCompanyAllocation: number;
              initialTargetShares: number;
              defaultTransactionFeePercent: number;
              defaultTransactionFeeVatPercent: number;
              finalDeal: boolean;
              fixedTargetShares?: number;
            }
          >();

          preservedAutoAllocationInput.forEach((fundAllocation) => {
            preservedDraftLookup.set(fundAllocation.trancheId, {
              minimumInvestorPortfolioSize:
                fundAllocation.minimumInvestorPortfolioSize ?? 0,
              maximumSingleCompanyAllocation:
                fundAllocation.maximumSingleCompanyAllocation ?? 0,
              initialTargetShares: fundAllocation.targetShares,
              defaultTransactionFeePercent:
                fundAllocation.transactionFeePercent ?? 0,
              defaultTransactionFeeVatPercent:
                fundAllocation.defaultTransactionFeeVatPercent ?? 0,
              finalDeal: fundAllocation.finalDeal,
              fixedTargetShares: fundAllocation.targetShares,
            });
          });

          setValue('autoAllocatingTranches', []);

          // Fetch fresh data for all selected tranches
          const freshTrancheData =
            await utils.eisAllocations.getTrancheDataByIds.fetch({
              trancheIds: selectedTrancheIds,
              allocationDate: allocationDate ?? new Date(),
            });

          if (freshTrancheData && freshTrancheData.length > 0) {
            // Create fresh data with preserved fields
            const updatedTranches = freshTrancheData.map(
              (trancheData, index) => {
                const preserved = preservedFields.get(trancheData._id);
                const preservedDraft = preservedDraftLookup.get(
                  trancheData._id,
                );

                return {
                  ...trancheData,
                  order: index,
                  isRemoved: false,
                  // Preserve the 4 specific fields if they exist, otherwise use defaults
                  minimumInvestorPortfolioSize:
                    preserved?.minimumInvestorPortfolioSize ??
                    preservedDraft?.minimumInvestorPortfolioSize ??
                    trancheData.minimumInvestorPortfolioSize,
                  maximumSingleCompanyAllocation:
                    preserved?.maximumSingleCompanyAllocation ??
                    preservedDraft?.maximumSingleCompanyAllocation ??
                    trancheData.maximumSingleCompanyAllocation,
                  initialTargetShares:
                    preserved?.initialTargetShares ??
                    preservedDraft?.initialTargetShares ??
                    0,
                  defaultTransactionFeePercent:
                    preserved?.defaultTransactionFeePercent ??
                    preservedDraft?.defaultTransactionFeePercent ??
                    trancheData.defaultTransactionFeePercent,
                  defaultTransactionFeeVatPercent:
                    preserved?.defaultTransactionFeeVatPercent ??
                    preservedDraft?.defaultTransactionFeeVatPercent ??
                    trancheData.defaultTransactionFeeVatPercent,
                  finalDeal:
                    preserved?.finalDeal ?? preservedDraft?.finalDeal ?? false,
                  fixedTargetShares:
                    preserved?.fixedTargetShares ??
                    preservedDraft?.fixedTargetShares ??
                    trancheData.fixedTargetShares,
                };
              },
            );

            // Replace all tranches with the fresh data
            setValue('autoAllocatingTranches', updatedTranches);
          }
        } catch (error) {
          console.error('Error handling investment date change', error);
        } finally {
          setIsDateChangeLoading(false);
        }
      };

      handleInvestmentDateChange();
    }
  }, [allocationDate?.getTime()]);

  // Form handlers for editable fields
  const handleTrancheFieldChange = (
    trancheId: string,
    field: keyof AutoAllocatingTranche,
    value: string | number | boolean | Record<string, unknown>,
  ) => {
    const index = autoAllocatingTranches.findIndex(
      (tranche) => tranche._id === trancheId,
    );
    if (index !== -1) {
      update(index, { ...autoAllocatingTranches[index], [field]: value });
    }
  };

  // Define the fields for the vertical table
  const fields = getAutoAllocationTableFields({
    handleTrancheFieldChange,
    initialTargetShareSelected,
    setInitialTargetShareSelected,
    resetInitialTargetShares,
    dealSharePrice,
    theme,
    fundAutoAllocationResults,
  });

  const { totalShares, totalInvestment, totalInvestmentIncTransactionFee } =
    visibleTranches.reduce<{
      totalShares: number;
      totalInvestment: number;
      totalInvestmentIncTransactionFee: number;
    }>(
      (acc, item) => {
        const calculatedValues = calculateTargetValues({
          trancheData: item,
          initialTargetShareSelected,
          dealSharePrice,
        });
        return {
          totalShares: acc.totalShares + calculatedValues.targetShares,
          totalInvestment:
            acc.totalInvestment + calculatedValues.targetInvestment,
          totalInvestmentIncTransactionFee:
            acc.totalInvestmentIncTransactionFee +
            calculatedValues.targetInvestmentIncTransactionFee,
        };
      },
      {
        totalShares: 0,
        totalInvestment: 0,
        totalInvestmentIncTransactionFee: 0,
      },
    );

  const handleRunAutoAllocation = () => {
    if (!allocationDate || !isValid(allocationDate)) {
      // Error notification refers to missing data in the previous form above Tranche Selection
      notification.error('An investment date is required.');
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      return;
    }

    runAutoAllocation({
      selectedCompanyId,
      allocationDate,
      dealSharePrice,
      autoAllocatingTranches: visibleTranches.map((tranche) => {
        const { targetShares, flexSharePrice } = calculateTargetValues({
          trancheData: tranche,
          initialTargetShareSelected,
          dealSharePrice,
        });

        return {
          _id: tranche._id,
          minimumInvestorPortfolioSize: tranche.minimumInvestorPortfolioSize,
          maximumSingleCompanyAllocation:
            tranche.maximumSingleCompanyAllocation,
          defaultTransactionFeePercent: tranche.defaultTransactionFeePercent,
          defaultTransactionFeeVatPercent:
            tranche.defaultTransactionFeeVatPercent,
          finalDeal: tranche.finalDeal,
          derivedTargetShares: targetShares,
          flexSharePrice,
        };
      }),
    });
  };

  return (
    <PageSection title="Set auto-allocation targets">
      <Spacing className="mb-2">
        <Typography variant="body2">
          To add and remove tranches, select them from the <i>Tranche(s)</i>{' '}
          dropdown above.
        </Typography>

        <VerticalTable
          fields={fields}
          data={visibleTranches}
          emptyMessage="No tranches selected. Select tranches to populate this table."
          isLoading={isLoadingTrancheForAllocation || isDateChangeLoading}
        />

        <Divider />
        <Grid>
          <Spacing>
            <GridContainer className="mt-2">
              <Grid>
                <Spacing size="sm">
                  <Row justify="space-between">
                    <Typography variant="body2" fontWeight="bold">
                      Total shares (all tranches)
                    </Typography>

                    <Typography variant="body2" fontWeight="bold">
                      {numberToDisplayString(totalShares)}
                    </Typography>
                  </Row>
                  <Row spacing="lg" justify="space-between">
                    <Typography variant="body2" fontWeight="bold">
                      Total investment (all tranches)
                    </Typography>

                    <Typography variant="body2" fontWeight="bold">
                      {numberToCurrencyString(totalInvestment)}
                    </Typography>
                  </Row>
                  <Row spacing="lg" justify="space-between">
                    <Typography variant="body2" fontWeight="bold">
                      Total investment (inc. transaction fee)
                    </Typography>

                    <Typography variant="body2" fontWeight="bold">
                      {numberToCurrencyString(totalInvestmentIncTransactionFee)}
                    </Typography>
                  </Row>
                </Spacing>
              </Grid>
            </GridContainer>

            <Typography variant="body2">
              Upon clicking <i>Run auto-allocation</i>, you will be taken to a
              summary page where you will be able to review and confirm the
              proposed allocation.
            </Typography>

            <Row justify="space-between">
              <Button
                variant="outlined"
                color="primary"
                className="mt-2"
                onClick={restartDialog.onOpen}
                disabled={isRunningAutoAllocation}
              >
                Clear and restart
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="mt-2"
                onClick={handleRunAutoAllocation}
                loading={isRunningAutoAllocation}
                disabled={
                  !isValid(allocationDate) ||
                  autoAllocatingTranches.length === 0 ||
                  !visibleTranches.some(
                    (tranche) => tranche.initialTargetShares !== 0,
                  )
                }
              >
                Run auto-allocation
              </Button>
            </Row>
          </Spacing>
        </Grid>
      </Spacing>

      <AlertDialog
        open={restartDialog.isOpen}
        onClose={restartDialog.onClose}
        content="Please note that the allocation will be lost if it is not saved as draft."
        title="Warning"
        onConfirm={handleClearAndRestart}
        btnLabels={{
          confirm: 'Clear and restart',
          cancel: 'Go back',
        }}
      />
    </PageSection>
  );
};

export default AutoAllocations;
